import { composeSetName, setLeaf, setsIn } from '@agavi/blocks';

import type { Block, TaskVariable } from '@/types/experience';

import { isVarUsed, renameVarInFormula } from '@/components/task/variable-utils';

/**
 * A set name free to take, given the ones already in use.
 *
 * Deliberately NOT `freeVariableName`, which appends `_2`: a set name becomes the front of a
 * mathjs identifier, so an underscore inside it would make `starA_2_mass` split ambiguously —
 * is the set `starA` or `starA_2`? A trailing digit keeps the composed name readable and the
 * split unambiguous.
 *
 * A NAME THAT ALREADY ENDS IN A NUMBER COUNTS ON FROM IT. Appending to `star1` gave `star12`,
 * which is not the twelfth of anything — it reads as a different number entirely. So the
 * trailing digits are the count: `star1` goes to `star2`, `star10` to `star11`. Only a name with
 * no number on it starts one, at 2, because the original is the implicit 1.
 *
 * Either way the result is checked against what exists and keeps climbing, so duplicating
 * `penguin` a second time gives `penguin3` rather than a second `penguin2`.
 */
export function freeSetName(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  const trailing = /^(.*?)(\d+)$/.exec(base);
  const stem = trailing ? trailing[1]! : base;
  let n = trailing ? Number(trailing[2]) + 1 : 2;
  while (used.has(`${stem}${n}`)) n++;
  return `${stem}${n}`;
}

/**
 * COPY A WHOLE SET, rewriting the references it makes to itself.
 *
 * This is the case that earns sets their keep — five stars each with mass, luminosity, radius,
 * lifetime and temperature is thirty rows nobody wants to type twice. It is also where a copy
 * goes quietly wrong: `starA_lum = {starA_mass}^3.5` duplicated verbatim gives a `starB_lum`
 * that is still computed from STAR A, which looks right in the panel and is wrong in every
 * question that uses it.
 *
 * So references to members of the copied set are re-pointed at the copy, and references to
 * anything else — a shared `{g}`, a task-level `{parallax}` — are left exactly as they are,
 * because those are genuinely shared and re-pointing them would be the opposite mistake.
 */
export function duplicateSet(
  set: string,
  variables: TaskVariable[],
  newId: () => string,
): { newSet: string; added: TaskVariable[] } {
  const members = variables.filter(v => v.set === set);
  const newSet = freeSetName(set, setsIn(variables));

  const added = members.map(m => {
    const leaf = setLeaf(m);
    let formula = m.formula;
    if (formula) {
      // Every member of the ORIGINAL set, re-pointed at the copy. Names outside the set are
      // untouched: they were not copied, so they are still the thing being referred to.
      for (const sibling of members) {
        formula = renameVarInFormula(formula, sibling.name, composeSetName(newSet, setLeaf(sibling)));
      }
    }
    return { ...m, id: newId(), set: newSet, name: composeSetName(newSet, leaf), ...(formula == null ? {} : { formula }) };
  });

  return { newSet, added };
}

/**
 * Why a set cannot be deleted, or false.
 *
 * Looser than the per-variable guard on purpose: a set goes as a unit, so a member being used by
 * ANOTHER MEMBER is not a reason to stop — both are leaving together. What blocks it is a
 * dependent that would be left behind: a block that still shows one of the members, or an
 * equation outside the set that stands on one.
 *
 * Without that distinction a set would be undeletable except in reverse dependency order, member
 * by member, which is the kind of thing that makes a delete button look broken.
 */
export function setDeleteBlocker(
  set: string,
  variables: TaskVariable[],
  blocks: Block[],
): { reason: 'blocks' | 'formula'; name: string } | false {
  const members = variables.filter(v => v.set === set);
  const memberNames = new Set(members.map(m => m.name));
  const outsiders = variables.filter(v => !memberNames.has(v.name));

  for (const m of members) {
    if (isVarUsed(m.name, blocks)) return { reason: 'blocks', name: m.name };
  }
  for (const m of members) {
    const dependent = outsiders.find(o => o.formula && formulaNames(o.formula).includes(m.name));
    if (dependent) return { reason: 'formula', name: dependent.name };
  }
  return false;
}

/** `{a}` references in a formula. Local rather than imported so this module does not drag the
 *  resolver's dependency graph in for one regex. */
function formulaNames(formula: string): string[] {
  return [...formula.matchAll(/\{([^}]+)\}/g)].map(m => (m[1] ?? '').trim()).filter(Boolean);
}
